import { cn } from "@/lib/utils";

/**
 * Editorial numbered section header.
 *
 * Mirrors the "01 — Identity" pattern from MyJarvis Dashboard's
 * DashboardArchitecturePage, but Tailwind-styled and RTL-aware so it
 * inherits the body Heebo/Assistant stack and reads cleanly in Hebrew.
 *
 * The number itself is ALWAYS Latin digits (existing site convention).
 * Eyebrow color defaults to rose to match the rest of the editorial
 * module's accent.
 */
export type NumberedSectionHeaderProps = {
  /** 1-based index of this section. Renders as 01, 02, 03 ... */
  index: number;
  /** Optional kicker shown after the number, e.g. "Identity". */
  kicker?: string;
  /** Section title — large, bold. */
  title: string;
  /** Optional subtitle / lede underneath. */
  subtitle?: string;
  /** Anchor id so TableOfContents links can target this header. */
  id?: string;
  /** Extra classes merged onto the wrapper. */
  className?: string;
};

export function NumberedSectionHeader({
  index,
  kicker,
  title,
  subtitle,
  id,
  className,
}: NumberedSectionHeaderProps) {
  const num = String(index).padStart(2, "0");

  return (
    <header id={id} className={cn("mb-6 scroll-mt-24", className)}>
      <p
        className={cn(
          "text-[11px] font-semibold uppercase",
          "tracking-[0.18em] text-rose-600",
          "mb-2",
        )}
      >
        <span className="font-mono ltr:mr-2 rtl:ml-2">{num}</span>
        {kicker ? <span className="opacity-80">— {kicker}</span> : null}
      </p>

      <h2
        className={cn(
          "text-2xl sm:text-[26px] font-bold tracking-tight",
          "text-stone-900 leading-[1.2]",
        )}
      >
        {title}
      </h2>

      {subtitle ? (
        <p
          className={cn(
            "mt-2 max-w-2xl text-[15px]",
            "leading-[1.65] text-stone-600",
          )}
        >
          {subtitle}
        </p>
      ) : null}
    </header>
  );
}
